import React, { useState } from "react";

import { Box, ListItem, SectionHeader, useBoxStyle } from "arkh";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";

import { UserLoggedInNavigatorNavigationProp } from "../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "../../../../common/navigationHeaderOptions";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import {
  OrgUserEditScreenQuery,
  WorkspaceRoleType,
} from "./__generated__/OrgUserEditScreenQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { FormattedMessage, useIntl } from "react-intl";
import { useAlert } from "../../../../common/Alert";
import { UserTypeSelect } from "./UserTypeSelect";
import { OrgUserEditScreenSetWorkspaceRolesMutation } from "./__generated__/OrgUserEditScreenSetWorkspaceRolesMutation.graphql";
import { WorkspaceRolesSelector } from "./WorkspaceRolesSelector";
import { ScrollView } from "react-native";
import { OrgUserEditScreenDeleteUserMutation } from "./__generated__/OrgUserEditScreenDeleteUserMutation.graphql";

type OrgUserEditScreenProps = {
  route: RouteProp<ParamList, "OrgUserEditScreen">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgUserEdit">,
    NativeStackNavigationProp<ParamList, "OrgUserEditScreen">
  >;
};

type ParamList = {
  OrgUserEditScreen: {
    id: string;
    email: string;
    name: string;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export const OrgUserEdit = withWebModal(() => {
  const getOptions = useNavigationHeaderOptions();
  return (
    <Stack.Navigator>
      <Stack.Screen
        component={OrgUserEditScreen}
        name="OrgUserEditScreen"
        options={({ navigation }) => ({
          ...getOptions({
            type: "SAVE",
            onCancel: () => {
              navigation.goBack();
            },
            saveLoading: false,
            disabled: true,
            onSave: () => {},
            defaultContentStyle: true,
            title: "",
          }),
        })}
      />
    </Stack.Navigator>
  );
}, true);

function OrgUserEditScreen(props: OrgUserEditScreenProps) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <OrgUserEditScreenComponent {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function OrgUserEditScreenComponent(
  props: OrgUserEditScreenProps & {
    fetchKey: string;
  }
) {
  const intl_tokens = useIntlTokens();
  useNavigationHeader({
    type: "CLOSE",
    title: intl_tokens.User,
    onClose: props.navigation.goBack,
  });
  const contentContainerStyle = useBoxStyle({
    space: "medium",
  });

  const data = useLazyLoadQuery<OrgUserEditScreenQuery>(
    graphql`
      query OrgUserEditScreenQuery($userId: String!) {
        organisation @required(action: THROW) {
          workspaces {
            ...OrgUserEditAccessSelect_workspaces
            ...WorkspaceRolesSelector_workspaces
          }
          user(id: $userId) {
            user {
              id
              name
              email
            }
            orgRole

            workspaceRoles {
              workspace {
                id
                name
                subWorkspaceCount
              }
              roleType
            }
          }
        }
      }
    `,
    {
      userId: props.route.params.id,
    },
    {
      fetchPolicy: "network-only",
      fetchKey: props.fetchKey,
    }
  );

  const [roles, setRoles] = useState<
    {
      roleType: WorkspaceRoleType;
      workspace:
        | {
            id: string;
            name: string;
            subWorkspaceCount: number;
          }
        | undefined;
    }[]
  >(data.organisation.user.workspaceRoles.slice());
  const [orgRole, setOrgRole] = useState(data.organisation.user.orgRole);

  const [setUserWorkspaceRoles, saveLoading] =
    useMutation<OrgUserEditScreenSetWorkspaceRolesMutation>(graphql`
      mutation OrgUserEditScreenSetWorkspaceRolesMutation(
        $userId: String!
        $workspaceRoles: [UserWorkspaceRoleInput!]!
        $orgRole: OrganisationRoleType!
      ) {
        organisation {
          setUserWorkspaceRoles(
            userId: $userId
            workspaceRoles: $workspaceRoles

            orgRole: $orgRole
          ) {
            user {
              id
              name
              email
            }
            workspaceRoles {
              workspace {
                id
                name
                subWorkspaceCount
              }
              roleType
            }
          }
        }
      }
    `);

  const [deleteUser] = useMutation<OrgUserEditScreenDeleteUserMutation>(graphql`
    mutation OrgUserEditScreenDeleteUserMutation($userId: String!) {
      organisation {
        deleteUser(userId: $userId) {
          user {
            id
          }
        }
      }
    }
  `);

  const intl = useIntl();
  const alert = useAlert();

  useNavigationHeader({
    type: "SAVE",
    onCancel: () => {
      props.navigation.goBack();
    },
    saveLoading,
    disabled: false,
    defaultContentStyle: true,
    more: [
      {
        label: intl_tokens.DeleteAccount,
        type: "button",
        onPress: () => {
          alert
            .confirm(
              intl_tokens.DeleteAccount,
              intl.formatMessage({
                defaultMessage: "Are you sure you want to delete this user?",
                description: "OrgUserEdit delete user confirmation",
              })
            )
            .then((result) => {
              if (result) {
                deleteUser({
                  variables: {
                    userId: props.route.params.id,
                  },
                  onCompleted: () => {
                    props.navigation.goBack();
                  },
                  onError: (error) => {
                    alert.error(error.message);
                  },
                });
              }
            });
        },
        color: "negative",
        icon: "user-remove",
      },
    ],
    onSave: () => {
      setUserWorkspaceRoles({
        variables: {
          userId: props.route.params.id,
          orgRole,
          workspaceRoles: roles.flatMap((wr) =>
            wr.workspace?.id !== undefined && wr.roleType !== undefined
              ? [
                  {
                    workspaceId: wr.workspace.id,
                    role: wr.roleType,
                  },
                ]
              : []
          ),
        },
        onCompleted: () => {
          props.navigation.goBack();
        },
        onError: (error) => {
          alert.error(
            intl.formatMessage({
              defaultMessage: "Could not save",
              description: "OrgUserEdit mutation error alert title",
            }),
            ""
          );
        },
      });
    },
    title: intl_tokens.User,
  });

  const usr = data.organisation.user.user;

  return (
    <ScrollView contentContainerStyle={contentContainerStyle}>
      <SectionHeader>
        <SectionHeader.Title>{intl_tokens.Name}</SectionHeader.Title>
      </SectionHeader>
      <ListItem isFree disabled border="border-primary">
        <ListItem.Title>{usr.name}</ListItem.Title>
      </ListItem>
      <Box spaceBottom="medium" />
      <SectionHeader>
        <SectionHeader.Title>{intl_tokens.Email}</SectionHeader.Title>
      </SectionHeader>
      <ListItem isFree disabled border="border-primary">
        <ListItem.Title>{usr.email}</ListItem.Title>
      </ListItem>
      <Box spaceBottom="medium" />
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Access"
            description="OrgUserEdit Access section header"
          />
        </SectionHeader.Title>
      </SectionHeader>
      <UserTypeSelect
        selected={orgRole}
        onSelect={(value) => setOrgRole(value)}
      />
      {orgRole === "WORKSPACE_USER" ? (
        <WorkspaceRolesSelector
          roles={roles}
          setRoles={setRoles}
          workspaces={data.organisation.workspaces}
        />
      ) : null}
      <Box space="xxlarge" />
    </ScrollView>
  );
}
